/**
 * La demande « confier un suivi à Otto » — ce que disaient les boutons « Créer une tâche » et
 * « Ajouter un rappel » d'une fiche ou d'une note.
 *
 * Décision de Gilles du 30/09/2026 (PLAN-FLUX-ET-COUVERTURE §1bis) : la tâche CRM n'est plus une
 * liste humaine, une mission d'Otto EST la tâche. Depuis le 12/10/2026 (« tout passe par le
 * Bureau »), le bouton ouvre « Nouvelle mission » avec cette demande écrite (`useAskOtto`) : la
 * mission existe déjà quand Otto la lit, il ne l'ouvre pas, il vérifie qu'une autre ne suit pas
 * déjà le sujet.
 *
 * Fonctions pures : c'est le contenu qui peut mentir. Une demande sans les identifiants fait
 * travailler Otto « dans le vide », sans compte ; une demande sans la consigne anti-doublon fait
 * suivre deux fois le même sujet.
 */

/** Les entités qu'un suivi peut viser — toujours par leur clé, jamais par leur nom (principe 12). */
export interface FollowUpRefs {
  accountId?: string | null;
  contactId?: string | null;
  opportunityId?: string | null;
  noteId?: string | null;
  appointmentId?: string | null;
}

export interface FollowUpContext extends FollowUpRefs {
  /** Ce qu'il faut suivre, en clair (« Relancer sur la proposition Forfait Pro »). */
  subject: string;
  /** Ce sur quoi porte le suivi, pour l'humain (« l'opportunité « Contrat FDS » »). */
  about?: string | null;
  /** Détails utiles (extrait de note, description). */
  details?: string | null;
  /** Échéance souhaitée, `YYYY-MM-DD`. */
  dueDate?: string | null;
}

const REF_LABELS: ReadonlyArray<readonly [keyof FollowUpRefs, string]> = [
  ['accountId', 'account_id'],
  ['contactId', 'contact_id'],
  ['opportunityId', 'opportunity_id'],
  ['noteId', 'note_id'],
  ['appointmentId', 'appointment_id'],
];

/** `[Contexte — account_id: …, opportunity_id: …]`, ou `''` sans aucune clé. */
export function contextLine(refs: FollowUpRefs): string {
  const parts = REF_LABELS.flatMap(([key, label]) => {
    const value = refs[key];
    return value ? [`${label}: ${value}`] : [];
  });
  return parts.length > 0 ? `[Contexte — ${parts.join(', ')}]` : '';
}

/** `2026-10-07` → `07/10/2026` ; une valeur inattendue est rendue telle quelle. */
export function frenchDay(isoDay: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDay);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : isoDay;
}

const MAX_DETAILS = 600;

/** A text cut to `max` characters (an excerpt: Otto reads the whole thing by its key). */
export function clip(text: string, max: number = MAX_DETAILS): string {
  const trimmed = text.trim();
  return trimmed.length > max ? `${trimmed.slice(0, max)}…` : trimmed;
}

const PLAN_INSTRUCTIONS =
  `Vérifie d'abord qu'aucune autre mission ne suit déjà ce sujet (workListMissions) : si c'est le ` +
  `cas, dis-le-moi (workAwaitHuman) au lieu de le suivre en double. Sinon refais le point (compte, ` +
  `échanges récents, notes), agis et planifie ton réveil à l'échéance ; si c'est à moi d'agir, ` +
  `pose-moi la question le moment venu (workAwaitHuman).`;

/** La demande d'un suivi unique (bouton « Confier un suivi à Otto » d'une fiche, d'une note…). */
export function buildFollowUpPrompt(ctx: FollowUpContext): string {
  const subject = ctx.subject.trim() || 'Suivi';
  const head = [
    `Prends en charge ce suivi : « ${subject} »${ctx.about ? ` (${ctx.about})` : ''}.`,
    contextLine(ctx),
    ctx.details?.trim() ? `Détails : « ${clip(ctx.details)} »` : '',
    ctx.dueDate ? `Échéance souhaitée : ${frenchDay(ctx.dueDate)}.` : '',
  ].filter(Boolean);
  return `${head.join('\n')}\n\n${PLAN_INSTRUCTIONS}`;
}
